import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import type { LatLngTuple } from 'leaflet';

export default function MapViewController({
  initialViewRef,
  imageBounds,
  planKey,
}: {
  initialViewRef: React.MutableRefObject<{ center: LatLngTuple; zoom: number } | null>;
  imageBounds?: any;
  planKey?: string;
}) {
  const map = useMap();
  const didInit = useRef(false);
  const prevPlanKey = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    if (initialViewRef.current) {
      map.setView(initialViewRef.current.center, initialViewRef.current.zoom, { animate: false });
    } else {
      initialViewRef.current = { center: map.getCenter() as any, zoom: map.getZoom() };
    }

    const save = () => {
      initialViewRef.current = { center: map.getCenter() as any, zoom: map.getZoom() };
    };
    map.on('zoomend moveend', save);

    return () => {
      map.off('zoomend moveend', save);
    };
  }, [map, initialViewRef]);

  useEffect(() => {
    if (!planKey || !imageBounds) return;
    if (prevPlanKey.current === planKey) return;
    prevPlanKey.current = planKey;

    map.fitBounds(imageBounds, { animate: false });
  }, [map, planKey, imageBounds]);

  useEffect(() => {
    const save = () => {
      initialViewRef.current = { center: map.getCenter() as any, zoom: map.getZoom() };
    };

    const restore = () => {
      if (document.visibilityState !== 'visible') return;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          map.invalidateSize({ pan: false });
          if (initialViewRef.current) {
            map.setView(initialViewRef.current.center, initialViewRef.current.zoom, { animate: false });
          }
        });
      });
    };

    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        save();
        return;
      }
      restore();
    };

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('blur', save);
    window.addEventListener('focus', restore);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('blur', save);
      window.removeEventListener('focus', restore);
    };
  }, [map, initialViewRef]);

  return null;
}
